var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase */
import { TweenMax, TweenLite } from 'gsap';
import { createAudio } from 'utils/audio-util';
import CommonAudio from 'mixins/CommonAudio';
import ErrorLogger from 'mixins/ErrorLogger';
import AssetLoader from 'mixins/AssetLoader';
import InactivityHelper from 'mixins/InactivityHelper';
import { activity, keyboard, activityTyping, application, mapState, mapGetters, mapMutations, mapActions } from '../store/helpers';
import mixins from 'vue-typed-mixins';
const { VUE_APP_S3_PREFIX } = process.env;
const ActivityManagerBase = () => mixins(CommonAudio, ErrorLogger, AssetLoader, InactivityHelper).extend({
    data() {
        return {
            assetsPrefix: VUE_APP_S3_PREFIX,
            voiceInstructionsPrefix: `${VUE_APP_S3_PREFIX}/sites/default/files/voice/instructions`,
            voiceCongratsPrefix: `${VUE_APP_S3_PREFIX}/sites/default/files/voice/congratulatory`,
            spokenWordsPrefix: `${VUE_APP_S3_PREFIX}/sites/default/files/voice/words-phrases`,
            sfx: undefined
        };
    },
    computed: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, mapState(activity, ['progress', 'viewedIntro', 'isRestarting', 'showIntro'])), mapState(activity, {
        roundsUntyped: 'rounds'
    })), { rounds() {
            return this.roundsUntyped;
        } }), mapState(activity, {
        activityData: state => state.data,
        // @ts-ignore TODO(kevin): where is spotCheck coming from?
        isSpotCheck: state => Boolean(state.data.spotCheck),
        voiceInstructions: state => state.instructions.voice
    })), mapGetters(activity, [
        'currentStep',
        'totalStepsInRound',
        'isLastStepInRound',
        'isLastRound',
        'isEndOfActivity',
        'isSpotCheckActivity',
        'showAssessmentAcceleratorIntro'
    ])), mapGetters(activity, {
        currentRoundUntyped: 'currentRound'
    })), { currentRound() {
            return this.currentRoundUntyped;
        } }), mapState(keyboard, {
        showKeyboardAtStart: state => state.settings.showKeyboardAtStart
    })), mapState(application, ['kwtMode', 'device'])), mapState(application, {
        userDevice: state => state.device
    })), mapGetters(application, [
        'ipadWithoutExternalKeyboard',
        'usingExternalKeyboard',
        'usingMobileChrome'
    ])), { isAssessmentAcceleratorTesterActivity() {
            return !!(this.isAssessmentAcceleratorActivity() && ['tester'].includes(this.kwtMode));
        },
        isAssessmentAcceleratorPreviewActivity() {
            return !!(this.isAssessmentAcceleratorActivity() &&
                ['preview', 'previewLite'].includes(this.kwtMode));
        },
        showActivityNextSectionPortal() {
            return (this.viewedIntro ||
                (!this.isAssessmentAcceleratorPreviewActivity &&
                    (!this.showAssessmentAcceleratorIntro || this.isAssessmentAcceleratorTesterActivity)));
        } }),
    watch: {
        viewedIntro(newViewed, oldViewed) {
            if (newViewed && !oldViewed) {
                if (this.showKeyboardAtStart) {
                    this.showKeyboard();
                }
                this.startActivity();
                this.playInstructions();
                if (this.isActiveAssessment) {
                    this.startTimer();
                }
            }
        },
        isRestarting(newValue, oldValue) {
            if (newValue && !oldValue) {
                TweenMax.to('.activity-stage', 0.5, { css: { autoAlpha: 1 } });
            }
        }
    },
    created() {
        return __awaiter(this, void 0, void 0, function* () {
            this.resetErrorLogger();
            // @ts-ignore implemented by consumer
            yield this.loadActivity();
            // @ts-ignore implemented by consumer
            if (this.setupKeyboard) {
                // @ts-ignore implemented by consumer
                this.setupKeyboard();
            }
            this.completeActivitySetup();
            if (!this.showIntro || this.isAssessmentAcceleratorTesterActivity) {
                this.setToolbarMode('inActivity');
                this.$nextTick(() => this.setIntroViewed());
            }
            if (this.isAssessmentAcceleratorPreviewActivity) {
                this.setToolbarMode('fullScreenOnly');
            }
        });
    },
    methods: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, mapMutations(activity, [
        'keepInstructionsOnScreen',
        'resetActivityStore',
        'createRounds',
        'incrementStep',
        'completeActivitySetup',
        'completeStep',
        'resetProgress',
        'resetViewedIntro',
        'resetCompletedSteps',
        'jumpToRound',
        'overwriteRoundSteps',
        'overwriteProgressStep',
        'setIntroViewed'
    ])), mapGetters(activity, ['isAssessmentAcceleratorActivity'])), mapMutations(activityTyping, [
        'resetAttemptedKey',
        'overwriteProgressText',
        'setFreeTextTyping'
    ])), mapMutations(keyboard, [
        'showKeyboard',
        'hideKeyboard',
        'releaseHoldKey',
        'resetKeyboard'
    ])), mapActions(activity, ['restartActivity'])), mapMutations(application, ['setKwtMode', 'setToolbarMode'])), { playInstructions() {
            if (!this.voiceInstructions)
                return;
            const audioInstructions = createAudio(this.voiceInstructions, {
                html5: !this.device.isMobile
            });
            const voiceIntro = TweenLite.to('.activity-instructions', 0, {
                delay: 1,
                onComplete: () => audioInstructions.play()
            });
            // @ts-ignore TODO(kevin): add Howler types
            window.Howler.ctx.resume();
            if (this.isAssessmentAcceleratorActivity() && this.sfx)
                this.sfx['INSTRUCTIONS'] = audioInstructions;
            return voiceIntro;
        },
        handleWrongKey(attempt) {
            // @ts-ignore implemented by consumer
            this.logError(attempt.character, this.expectedKey);
            this.sndError();
        },
        startActivity() {
            this.resetErrorLogger();
            this.errorLoggerStart();
            // @ts-ignore implemented by consumer
            this.startRound();
        },
        playStepSound(sfx) {
            const { roundIndex, stepIndex } = this.progress;
            sfx.soundsPerRound[roundIndex][stepIndex].play();
        },
        nextStep({ isFirstStep = false, enableKeyboard = true } = {}) {
            // @ts-ignore implemented by consumer
            if (enableKeyboard && this.enableKeyboard) {
                // @ts-ignore implemented by consumer
                this.enableKeyboard();
            }
            // @ts-ignore implemented by consumer
            this.setCurrentStep({ isFirstStep });
        },
        completeActivity(playCongrats = true) {
            if (playCongrats) {
                this.playCongrats();
            }
            this.processEndOfActivity();
        },
        processEndOfActivity() {
            this.errorLoggerStop();
            this.resetAttemptedKey();
            if (this.hideKeyboard) {
                this.hideKeyboard();
            }
            // @ts-ignore implemented by consumer
            if (this.isTouchHold) {
                this.releaseHoldKey();
            }
            TweenMax.to('.activity-stage', 2, {
                autoAlpha: 0,
                onComplete: () => {
                    const isPreview = this.kwtMode === 'preview' || this.kwtMode === 'previewLite';
                    if (this.kwtMode === 'curriculum') {
                        if (this.isSpotCheck) {
                            this.$router.replace({ path: '/spotcheck-result', params: {} });
                        }
                        else {
                            this.$router.replace({ path: '/activity-trophy', params: {} });
                        }
                    }
                    else if (isPreview && this.isSpotCheckActivity) {
                        this.$router.replace({ path: '/spotcheck-result', params: {} });
                    }
                    else {
                        this.handleModeRedirect();
                    }
                }
            });
        },
        handleModeRedirect() {
            if (this.kwtMode !== 'curriculum') {
                this.resetProgress();
                this.resetViewedIntro();
            }
            switch (this.kwtMode) {
                case 'tester':
                    this.resetActivityStore();
                    this.resetKeyboard();
                    this.$router.push({ path: '/tester' });
                    break;
                case 'preview':
                case 'previewLite':
                    this.restartActivity(false);
                    break;
                default:
                    this.setKwtMode('unknown');
                    this.resetActivityStore();
                    this.resetKeyboard();
                    this.$router.push({ path: '/tester' });
            }
        },
        loadAAAudios(audiosMap) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPaths = Object.keys(audiosMap).map(key => audiosMap[key]);
                this.queueAudio(soundPaths);
                const cache = yield this.downloadAudio();
                const cachedAudiosMap = {};
                Object.keys(audiosMap).forEach(key => (cachedAudiosMap[key] = cache.get(audiosMap[key])));
                return cachedAudiosMap;
            });
        },
        playAudio(audioKey) {
            this.stopAllAudio();
            return new Promise((resolve, reject) => {
                var _a;
                if (!((_a = this.sfx) === null || _a === void 0 ? void 0 : _a[audioKey]))
                    return reject(new Error('non-existent sfx object or audio key'));
                this.sfx[audioKey].once('end', () => resolve());
                this.sfx[audioKey].once('playerror', () => reject());
                this.sfx[audioKey].play();
            });
        },
        stopAllAudio() {
            if (this.sfx)
                Object.keys(this.sfx).map(key => { var _a; return (_a = this.sfx) === null || _a === void 0 ? void 0 : _a[key].stop(); });
        } })
});
const ActivityManager = ActivityManagerBase();
export const ActivityManagerTyped = ActivityManagerBase;
export default ActivityManager;
